import React, { useState } from 'react';
import { supabase } from '../../../shared/lib/supabase';
import { Compass, Eye, EyeOff, Check, X } from 'lucide-react';

export function PasswordSetupPage() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Get email and token from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const email = urlParams.get('email') || 'your email';
  const token = urlParams.get('token'); // For team invitations

  // Password validation rules
  const passwordRules = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
  };

  const isPasswordValid = Object.values(passwordRules).every(rule => rule);
  const passwordsMatch = password === confirmPassword && password.length > 0;
  const canSubmit = isPasswordValid && passwordsMatch && !loading;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    try {
      setLoading(true);
      setError(null);

      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        setError('Error setting password: ' + error.message);
      } else {
        setSuccess(true);
        // If there's a token, redirect back to accept invitation
        setTimeout(() => {
          if (token) {
            window.location.href = `/accept-invitation?token=${token}`;
          } else {
            window.location.href = '/';
          }
        }, 2000);
      }
    } catch (err) {
      setError('Error setting password');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-gray-50">
        <div className="max-w-md w-full mx-4">
          <div className="glass-card rounded-sm p-8 shadow-xl text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Password Set Successfully!</h2>
            <p className="text-gray-600 mb-6">
              {token 
                ? 'Your password has been set. Redirecting you to accept your team invitation...'
                : 'Your password has been set. You can now log in with your email and password.'
              }
            </p>
            <p className="text-sm text-gray-500">
              {token ? 'Redirecting to team invitation...' : 'Redirecting to login...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-gray-50">
      <div className="max-w-md w-full mx-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="glass-button rounded-sm p-3 mr-3">
              <Compass className="h-8 w-8 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              anthrogen:drift
            </h1>
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Set Your Password</h2>
          <p className="text-gray-600">
            Create a secure password for {email}
            {token && <span className="block text-sm text-blue-600 mt-1">Complete your account to join the team</span>}
          </p>
        </div>

        {/* Main Form */}
        <div className="glass-card rounded-sm p-8 shadow-xl">
          <div className="space-y-6">
            {/* Password Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Password Requirements */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Password must contain:</p>
              <div className="grid grid-cols-1 gap-1 text-sm">
                <div className={`flex items-center gap-2 ${passwordRules.length ? 'text-green-600' : 'text-gray-500'}`}>
                  {passwordRules.length ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                  At least 8 characters
                </div>
                <div className={`flex items-center gap-2 ${passwordRules.uppercase ? 'text-green-600' : 'text-gray-500'}`}>
                  {passwordRules.uppercase ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                  One uppercase letter (A-Z)
                </div>
                <div className={`flex items-center gap-2 ${passwordRules.lowercase ? 'text-green-600' : 'text-gray-500'}`}>
                  {passwordRules.lowercase ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                  One lowercase letter (a-z)
                </div>
                <div className={`flex items-center gap-2 ${passwordRules.number ? 'text-green-600' : 'text-gray-500'}`}>
                  {passwordRules.number ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                  One number (0-9)
                </div>
                <div className={`flex items-center gap-2 ${passwordRules.special ? 'text-green-600' : 'text-gray-500'}`}>
                  {passwordRules.special ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                  One special character (!@#$%^&*)
                </div>
              </div>
            </div>

            {/* Confirm Password Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`w-full px-4 py-3 pr-12 border rounded-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    confirmPassword && !passwordsMatch ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {confirmPassword && !passwordsMatch && (
                <p className="text-red-600 text-sm mt-1">Passwords do not match</p>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-sm p-3">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className={`w-full py-3 px-6 rounded-sm font-medium transition-all duration-200 ${
                canSubmit
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {loading ? 'Setting Password...' : 'Set Password'}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            Powered by Odyssey™ AI
          </p>
        </div>
      </div>
    </div>
  );
}